import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded';

import { User } from 'src/interfaces/iuser';
import {
  COLOR_BACKGROUND_1,
  COLOR_BACKGROUND_2,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_100,
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_100,
  COLOR_PRIMARY_2_200,
  COLOR_TEXT_2,
  COLOR_TEXT_GREY,
} from 'src/utils/colors';
import {
  AI_SEARCH_SORT_BY_VALUES,
  AiSearchApiPlan,
  AiSearchApiPlanStep,
  AiSearchConversationTurn,
  AiSearchCoordinatesFallback,
  AiSearchExecutionMarketCall,
  AiSearchExecutionSummary,
  AiSearchFollowUpContext,
  AiSearchMarketClarificationChoice,
  AiSearchMarketScanCombinedChoice,
  AiSearchMarketSuggestion,
  AiSearchMicroLocationContext,
  AiSearchRequestContext,
  AiSearchResponseMetadata,
  AiSearchScanDepth,
  AiSearchSearchPlan,
  AiSearchSortBy,
  createAiSearchMessage,
  readAiSearchMessage,
} from 'src/App/Dashboards/Market/Pages/HomeChatPage/api';

const EXAMPLE_PROMPTS = [
  'Which area in Melbourne has the best occupancy rate?',
  'What is the occupancy rate and ADR for 2-bedroom homes with a pool in downtown Austin?',
  'Compare Bali vs Bangkok for Airbnb investment.',
  'Find the best Mediterranean Airbnb markets outside big tourist cities.',
  'Analyze this 2-bedroom luxury property in San Diego: 4648 Idaho St, San Diego, CA 92116.',
  'What does ADR mean?',
];

const UNSUPPORTED_EXAMPLE_PROMPTS = [
  'Best Airbnb markets in Florida',
  'Compare Lisbon vs Barcelona',
  'Show occupancy in Austin',
];

type HomeChatPageProps = {
  user: User;
  matches: boolean;
  isLoggedIn: boolean;
  setLoginModalOpen: (open: boolean) => void;
};

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  metadata?: AiSearchResponseMetadata;
  searchPlan?: AiSearchSearchPlan;
};

type ContextualLink = {
  label: string;
  url: string;
};

type OccupancyInsight = {
  marketName: string;
  occupancyRate: number;
  timeframe: 'ltm' | 'latest_month' | 'current_month' | 'unspecified';
  timeframeSource: 'api' | 'inferred' | 'missing';
};

const CONVERSATION_HISTORY_LIMIT = 10;

const detectOccupancyTimeframe = (text: string): OccupancyInsight['timeframe'] => {
  const normalized = text.toLowerCase();
  if (/(ltm|last 12 months|trailing 12 months|past 12 months)/.test(normalized)) {
    return 'ltm';
  }
  if (/(latest completed month|last completed month|previous month|last month)/.test(normalized)) {
    return 'latest_month';
  }
  if (/(currently|current month|this month|month-to-date|mtd)/.test(normalized)) {
    return 'current_month';
  }
  return 'unspecified';
};

const getTimeframeFromApiMetadata = (
  metadata?: Record<string, unknown>
): OccupancyInsight['timeframe'] | null => {
  if (!metadata) {
    return null;
  }

  const candidateKeys = [
    'timeframe',
    'time_frame',
    'period',
    'metric_period',
    'occupancy_timeframe',
    'reporting_period',
    'window',
    'date_range',
  ];

  const findTimeframeValue = (obj: Record<string, unknown>): string | null => {
    for (const key of candidateKeys) {
      const value = obj[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return null;
  };

  const normalizeTimeframe = (value: string): OccupancyInsight['timeframe'] => {
    const normalized = value.toLowerCase();
    if (/(^|[^a-z])(ltm|last 12 months|trailing 12 months|past 12 months)([^a-z]|$)/.test(normalized)) {
      return 'ltm';
    }
    if (/(latest completed month|last completed month|previous month|last month)/.test(normalized)) {
      return 'latest_month';
    }
    if (/(current month|this month|month-to-date|mtd)/.test(normalized)) {
      return 'current_month';
    }
    return 'unspecified';
  };

  const directValue = findTimeframeValue(metadata);
  if (directValue) {
    return normalizeTimeframe(directValue);
  }

  const metadataSources = ['response_fields', 'request_fields', 'result', 'data'];
  for (const sourceKey of metadataSources) {
    const sourceValue = metadata[sourceKey];
    if (sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
      const nestedValue = findTimeframeValue(sourceValue as Record<string, unknown>);
      if (nestedValue) {
        return normalizeTimeframe(nestedValue);
      }
    }
  }

  return null;
};

const getTimeframeLabel = (timeframe: OccupancyInsight['timeframe']) => {
  if (timeframe === 'ltm') {
    return 'LTM (last 12 months)';
  }
  if (timeframe === 'latest_month') {
    return 'Latest completed month';
  }
  if (timeframe === 'current_month') {
    return 'Current month to date';
  }
  return 'Not specified in this answer';
};

const getTimeframeConfidenceNote = (
  timeframe: OccupancyInsight['timeframe'],
  timeframeSource: OccupancyInsight['timeframeSource']
) => {
  if (timeframeSource === 'api') {
    return 'From API response metadata.';
  }
  if (timeframe === 'unspecified' || timeframeSource === 'missing') {
    return 'Ask for LTM, latest completed month, or current month for a precise period.';
  }
  return 'Inferred from answer wording.';
};

const extractOccupancyInsight = (
  text: string,
  metadata?: Record<string, unknown>
): OccupancyInsight | null => {
  const normalizedText = text.replace(/\s+/g, ' ').trim();
  const match = normalizedText.match(/occupancy rate in (.+?) is .*?(\d+(?:\.\d+)?)%/i);
  if (!match) {
    return null;
  }

  const marketName = match[1].trim();
  const occupancyRate = Number(match[2]);
  if (Number.isNaN(occupancyRate)) {
    return null;
  }

  const apiTimeframe = getTimeframeFromApiMetadata(metadata);
  const inferredTimeframe = detectOccupancyTimeframe(normalizedText);

  return {
    marketName,
    occupancyRate,
    timeframe: apiTimeframe || inferredTimeframe,
    timeframeSource: apiTimeframe ? 'api' : inferredTimeframe === 'unspecified' ? 'missing' : 'inferred',
  };
};

const formatMarketCtaLabel = (label: string) => {
  const trimmedLabel = label.trim();
  if (
    /^market\s+\d+$/i.test(trimmedLabel) ||
    /^scanned\s+market\s+\d+$/i.test(trimmedLabel)
  ) {
    return 'View market dashboard';
  }
  return trimmedLabel;
};

const getCategoryLabel = (category?: AiSearchSearchPlan['category']) => {
  if (!category) {
    return 'Analysis';
  }
  if (category === 'market_scan') {
    return 'Market scan';
  }
  if (category === 'market_analysis') {
    return 'Market analysis';
  }
  if (category === 'property_analysis') {
    return 'Property analysis';
  }
  if (category === 'terms_definition') {
    return 'Terms and definitions';
  }
  return 'Not supported';
};

const getResolvedMarketNames = (searchPlan?: AiSearchSearchPlan): string[] => {
  const resolved = searchPlan?.market_resolution?.resolved_markets || [];
  return resolved
    .map(market => market.name?.trim())
    .filter((name): name is string => Boolean(name));
};

// Friendly display name for the underlying tool. The api_plan UI never
// surfaces raw tool identifiers (`get_market_summary`) to users; this maps
// them to the same vocabulary the "Data basis" section already uses.
const API_PLAN_TOOL_LABELS: Record<string, string> = {
  get_market_summary: 'Market summary',
  search_listings_sample: 'Listings radius sample',
};

const getApiPlanToolLabel = (tool: string): string =>
  API_PLAN_TOOL_LABELS[tool] || tool;

// Pure helper so the preview's content can be unit-tested without rendering
// JSX. Returns the list of `{tool, label}` rows the preview should show.
export const getApiPlanPreviewRows = (
  searchPlan?: AiSearchSearchPlan | null,
): Array<{ stepId: string; toolLabel: string; label: string }> => {
  const apiPlan: AiSearchApiPlan | undefined = searchPlan?.api_plan;
  if (!apiPlan || !Array.isArray(apiPlan.steps)) {
    return [];
  }
  return apiPlan.steps
    .filter((step): step is AiSearchApiPlanStep => Boolean(step) && typeof step === 'object')
    .map(step => ({
      stepId: step.step_id,
      toolLabel: getApiPlanToolLabel(step.tool),
      label: step.label_for_summary,
    }));
};

const isNotSupportedSearchPlan = (searchPlan?: AiSearchSearchPlan) =>
  searchPlan?.category === 'not_supported';

const getCoordinatesFallback = (metadata?: AiSearchResponseMetadata): AiSearchCoordinatesFallback | null => {
  const fallback = metadata?.coordinates_fallback;
  if (!fallback || typeof fallback !== 'object') {
    return null;
  }
  return fallback;
};

const getFollowUpContext = (metadata?: AiSearchResponseMetadata): AiSearchFollowUpContext | null => {
  const context = metadata?.follow_up_context;
  if (!context || typeof context !== 'object') {
    return null;
  }
  const typedContext = context as Partial<AiSearchFollowUpContext>;
  if (
    typedContext.type === 'property_bedrooms' &&
    typeof typedContext.property_address === 'string' &&
    typedContext.property_address.trim()
  ) {
    return {
      type: 'property_bedrooms',
      property_address: typedContext.property_address.trim(),
    };
  }
  if (typedContext.type === 'market_scan_depth') {
    const choice = typedContext.choice;
    if (choice === 'standard' || choice === 'deep_research') {
      return { type: 'market_scan_depth', choice };
    }
  }
  return null;
};

const getMarketScanDepthFollowUp = (metadata?: AiSearchResponseMetadata) => {
  const context = metadata?.follow_up_context;
  if (!context || typeof context !== 'object') {
    return null;
  }
  const typed = context as { type?: string; awaiting_choice?: boolean };
  if (typed.type === 'market_scan_depth' && typed.awaiting_choice) {
    return { awaiting_choice: true as const };
  }
  return null;
};

const COUNTRY_NAME_BY_CODE: Record<string, string> = {
  US: 'United States',
  GB: 'United Kingdom',
  FR: 'France',
  DE: 'Germany',
  IT: 'Italy',
  ES: 'Spain',
  PT: 'Portugal',
  GR: 'Greece',
  HR: 'Croatia',
  MT: 'Malta',
  CY: 'Cyprus',
  TR: 'Turkey',
  NL: 'Netherlands',
  BE: 'Belgium',
  CH: 'Switzerland',
  AT: 'Austria',
  PL: 'Poland',
  CZ: 'Czechia',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  IS: 'Iceland',
  IE: 'Ireland',
  JP: 'Japan',
  KR: 'South Korea',
  CN: 'China',
  TW: 'Taiwan',
  HK: 'Hong Kong',
  TH: 'Thailand',
  VN: 'Vietnam',
  ID: 'Indonesia',
  PH: 'Philippines',
  MY: 'Malaysia',
  SG: 'Singapore',
  IN: 'India',
  AU: 'Australia',
  NZ: 'New Zealand',
  BR: 'Brazil',
  AR: 'Argentina',
  MX: 'Mexico',
  CL: 'Chile',
  CO: 'Colombia',
  PE: 'Peru',
  CA: 'Canada',
  ZA: 'South Africa',
  AE: 'United Arab Emirates',
  SA: 'Saudi Arabia',
  EG: 'Egypt',
  MA: 'Morocco',
};

export const formatCountryChipLabel = (code: string): string => {
  const upper = (code || '').trim().toUpperCase();
  if (!upper) {
    return '';
  }
  return COUNTRY_NAME_BY_CODE[upper] || upper;
};

const SORT_BY_LABELS: Record<AiSearchSortBy, string> = {
  supply: 'Most supply',
  short_term_rental_yield: 'Best yield',
  rent_gap: 'Largest rent gap',
  revenue: 'Top revenue',
  yoy_supply_change: 'Fastest supply growth',
  yoy_revenue_change: 'Fastest revenue growth',
  market_grade: 'Highest market grade',
};

export const formatSortByChipLabel = (sortBy: AiSearchSortBy | string): string => {
  if ((AI_SEARCH_SORT_BY_VALUES as readonly string[]).includes(sortBy)) {
    return SORT_BY_LABELS[sortBy as AiSearchSortBy];
  }
  return sortBy || '';
};

type MarketScanCombinedContext = {
  countryCodes: string[];
  sortByOptions: AiSearchSortBy[];
  selectedSortBy: AiSearchSortBy | '';
  depthOptions: AiSearchScanDepth[];
};

export const getMarketScanCombinedFollowUp = (
  metadata?: AiSearchResponseMetadata
): MarketScanCombinedContext | null => {
  const context = metadata?.follow_up_context;
  if (!context || typeof context !== 'object') {
    return null;
  }
  const typed = context as Partial<AiSearchFollowUpContext> & {
    country_codes?: string[];
    sort_by_options?: AiSearchSortBy[];
    selected_sort_by?: AiSearchSortBy | '';
    depth_options?: AiSearchScanDepth[];
  };
  if (typed.type !== 'market_scan_combined') {
    return null;
  }
  const countryCodes = Array.isArray(typed.country_codes)
    ? typed.country_codes.filter(code => typeof code === 'string' && code.trim()).map(code => code.toUpperCase())
    : [];
  const sortByOptions =
    Array.isArray(typed.sort_by_options) && typed.sort_by_options.length > 0
      ? (typed.sort_by_options.filter(opt =>
          (AI_SEARCH_SORT_BY_VALUES as readonly string[]).includes(opt)
        ) as AiSearchSortBy[])
      : ([...AI_SEARCH_SORT_BY_VALUES] as AiSearchSortBy[]);
  const selectedSortBy: AiSearchSortBy | '' = (AI_SEARCH_SORT_BY_VALUES as readonly string[]).includes(
    typed.selected_sort_by as string
  )
    ? (typed.selected_sort_by as AiSearchSortBy)
    : '';
  const depthOptions =
    Array.isArray(typed.depth_options) && typed.depth_options.length > 0
      ? (typed.depth_options.filter(opt => opt === 'standard' || opt === 'deep_research') as AiSearchScanDepth[])
      : (['standard', 'deep_research'] as AiSearchScanDepth[]);
  return { countryCodes, sortByOptions, selectedSortBy, depthOptions };
};

type MarketClarificationContext = {
  suggestions: AiSearchMarketSuggestion[];
  inputTerms: string[];
};

export const getMarketClarificationFollowUp = (
  metadata?: AiSearchResponseMetadata
): MarketClarificationContext | null => {
  const context = metadata?.follow_up_context;
  if (!context || typeof context !== 'object') {
    return null;
  }
  const typed = context as Partial<AiSearchFollowUpContext> & {
    suggestions?: AiSearchMarketSuggestion[];
    input_terms?: string[];
  };
  if (typed.type !== 'market_clarification') {
    return null;
  }
  const suggestions = Array.isArray(typed.suggestions)
    ? typed.suggestions.filter(s => s && typeof s === 'object' && typeof s.market_id === 'number')
    : [];
  const inputTerms = Array.isArray(typed.input_terms)
    ? typed.input_terms.filter(t => typeof t === 'string' && t.trim())
    : [];
  return { suggestions, inputTerms };
};

const isDepthGateMessage = (metadata?: AiSearchResponseMetadata): boolean => {
  if (!metadata || typeof metadata !== 'object') {
    return false;
  }
  if (getMarketScanDepthFollowUp(metadata)?.awaiting_choice) {
    return true;
  }
  if (getMarketScanCombinedFollowUp(metadata)) {
    return true;
  }
  if (getMarketClarificationFollowUp(metadata)) {
    return true;
  }
  if (metadata.stage1_only === true) {
    return true;
  }
  return metadata.model === 'market_scan_depth_gate';
};

const getDataBasisLineForMessage = (
  metadata?: AiSearchResponseMetadata,
  searchPlan?: AiSearchSearchPlan
): string | null => {
  if (isDepthGateMessage(metadata)) {
    return null;
  }
  return formatDataBasisLine(metadata, searchPlan);
};

const getScanManifest = (metadata?: AiSearchResponseMetadata) => {
  const manifest = metadata?.scan_manifest;
  if (!manifest || typeof manifest !== 'object') {
    return null;
  }
  if (manifest.scan_source !== 'getBestMarkets') {
    return null;
  }
  return manifest;
};

const getExecutionSummary = (
  metadata?: AiSearchResponseMetadata
): AiSearchExecutionSummary | null => {
  const summary = metadata?.execution_summary;
  if (!summary || typeof summary !== 'object') {
    return null;
  }
  return summary;
};

const getMicroLocationContext = (
  metadata?: AiSearchResponseMetadata
): AiSearchMicroLocationContext | null => {
  const context = metadata?.micro_location_context;
  if (!context || typeof context !== 'object') {
    return null;
  }
  return context;
};

// Returns one context per micro-area card to render. Recent backends emit
// `micro_location_contexts` with N entries (one per `micro_locations` queue
// item); older backends only have `micro_location_context` (singular), in
// which case we wrap it as a one-element list so the render path stays
// unified.
export const getMicroLocationContexts = (
  metadata?: AiSearchResponseMetadata
): AiSearchMicroLocationContext[] => {
  const list = metadata?.micro_location_contexts;
  if (Array.isArray(list) && list.length > 0) {
    return list.filter(
      (ctx): ctx is AiSearchMicroLocationContext =>
        Boolean(ctx) && typeof ctx === 'object'
    );
  }
  const singular = metadata?.micro_location_context;
  if (singular && typeof singular === 'object') {
    return [singular];
  }
  return [];
};

const getResolvedMarketById = (
  marketId: number,
  searchPlan?: AiSearchSearchPlan
) =>
  searchPlan?.market_resolution?.resolved_markets?.find(market => market.market_id === marketId);

export const formatSampleSizeLabel = (sampleSize?: number): string => {
  if (typeof sampleSize !== 'number' || !Number.isFinite(sampleSize) || sampleSize <= 0) {
    return '';
  }
  return `n=${sampleSize.toLocaleString()} listings`;
};

const formatMarketBasisLabel = (
  call: Pick<AiSearchExecutionMarketCall, 'market_id' | 'market_name' | 'sample_size'>,
  searchPlan?: AiSearchSearchPlan
): string => {
  const resolved = getResolvedMarketById(call.market_id, searchPlan);
  const name = (call.market_name || resolved?.name || '').trim();
  const country = (resolved?.country || '').trim();
  const sampleLabel = formatSampleSizeLabel(call.sample_size);
  const base = name && country ? `${name}, ${country}` : name;
  if (!base) {
    return sampleLabel;
  }
  return sampleLabel ? `${base} (${sampleLabel})` : base;
};

const formatPropertyTypeLabel = (value: string) => value.replace(/_/g, ' ');

const formatAmenityLabels = (amenity?: Record<string, boolean>): string[] => {
  if (!amenity) {
    return [];
  }
  return Object.entries(amenity)
    .filter(([, enabled]) => Boolean(enabled))
    .map(([key]) => key.replace(/_/g, ' '));
};

const formatFiltersFragment = (
  filters?: AiSearchExecutionMarketCall['filters'] | AiSearchSearchPlan['filters']
): string => {
  if (!filters) {
    return '';
  }
  const parts: string[] = [];
  const bedrooms = filters.bedrooms;
  if (Array.isArray(bedrooms) && bedrooms.length > 0) {
    parts.push(`${bedrooms.join(', ')} BR`);
  } else if (typeof bedrooms === 'number') {
    parts.push(`${bedrooms} BR`);
  }
  const propertyTypes = filters.property_type;
  if (Array.isArray(propertyTypes) && propertyTypes.length > 0) {
    parts.push(propertyTypes.map(formatPropertyTypeLabel).join(', '));
  } else if (typeof propertyTypes === 'string' && propertyTypes.trim()) {
    parts.push(formatPropertyTypeLabel(propertyTypes.trim()));
  }
  const amenities = formatAmenityLabels(
    filters.amenity && typeof filters.amenity === 'object' ? filters.amenity : undefined
  );
  if (amenities.length > 0) {
    parts.push(amenities.join(', '));
  }
  return parts.join(', ');
};

const formatCoordinatePair = (latitude?: number, longitude?: number): string => {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    return '';
  }
  if (Math.abs(latitude) < 1e-9 && Math.abs(longitude) < 1e-9) {
    return '';
  }
  return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
};

const formatMicroLocationSampleMetricsLabel = (
  context?: AiSearchMicroLocationContext | null
): string => {
  const sampleMetrics = context?.sample_metrics;
  const microOcc = sampleMetrics?.avg_occupancy_rate;
  const microAdr = sampleMetrics?.avg_adr;
  if (typeof microOcc !== 'number' || typeof microAdr !== 'number') {
    return '';
  }
  return `Sample occ ${microOcc}% · ADR $${Math.round(microAdr)}`;
};

export const formatMicroLocationSampleSizeLabel = (
  context?: AiSearchMicroLocationContext | null
): string => {
  if (!context) {
    return '';
  }
  const sampled =
    typeof context.selected_listing_sample_count === 'number'
      ? context.selected_listing_sample_count
      : undefined;
  const total =
    typeof context.selected_total_count === 'number' ? context.selected_total_count : undefined;
  if (typeof sampled !== 'number' && typeof total !== 'number') {
    return '';
  }
  if (typeof sampled === 'number' && typeof total === 'number' && total > sampled) {
    return `Samples: ${sampled.toLocaleString()} of ${total.toLocaleString()} listings`;
  }
  if (typeof sampled === 'number' && sampled > 0) {
    return `Samples: ${sampled.toLocaleString()} listings`;
  }
  if (typeof total === 'number' && total > 0) {
    return `Samples: ${total.toLocaleString()} listings`;
  }
  return '';
};

const formatMicroLocationBasis = (
  metadata?: AiSearchResponseMetadata,
  searchPlan?: AiSearchSearchPlan
): string => {
  const context = getMicroLocationContext(metadata);
  if (!context?.used) {
    return '';
  }
  const executionSummary = getExecutionSummary(metadata);
  const firstCall = executionSummary?.market_calls?.[0];

  const contextCoordinates = context?.coordinates || {};
  const contextLatitude =
    typeof contextCoordinates.latitude === 'number' ? contextCoordinates.latitude : undefined;
  const contextLongitude =
    typeof contextCoordinates.longitude === 'number' ? contextCoordinates.longitude : undefined;
  const planLatitude =
    typeof searchPlan?.micro_location?.latitude === 'number'
      ? searchPlan.micro_location.latitude
      : undefined;
  const planLongitude =
    typeof searchPlan?.micro_location?.longitude === 'number'
      ? searchPlan.micro_location.longitude
      : undefined;
  const latitude =
    (typeof firstCall?.search_latitude === 'number' && firstCall.search_latitude) ||
    contextLatitude ||
    planLatitude;
  const longitude =
    (typeof firstCall?.search_longitude === 'number' && firstCall.search_longitude) ||
    contextLongitude ||
    planLongitude;
  const coordinatesText = formatCoordinatePair(latitude, longitude);

  const selectedRadius =
    (typeof context?.selected_radius_meters === 'number' && context.selected_radius_meters) ||
    (typeof firstCall?.radius_meters === 'number' && firstCall.radius_meters) ||
    (typeof searchPlan?.micro_location?.radius_hint_meters === 'number' &&
      searchPlan.micro_location.radius_hint_meters);

  const rawPhrase = (context?.raw_phrase || '').trim();
  const parts: string[] = [];
  if (rawPhrase) {
    parts.push(rawPhrase);
  }
  if (coordinatesText) {
    parts.push(`micro-location ${coordinatesText}`);
  }
  if (typeof selectedRadius === 'number' && selectedRadius > 0) {
    parts.push(`Radius: ${selectedRadius}m`);
  }
  const sampleLabel = formatMicroLocationSampleMetricsLabel(context);
  if (sampleLabel) {
    parts.push(sampleLabel);
  }
  return parts.join(' · ');
};

const formatScanManifestLine = (
  metadata?: AiSearchResponseMetadata,
  searchPlan?: AiSearchSearchPlan
): string | null => {
  const manifest = getScanManifest(metadata);
  if (!manifest) {
    return null;
  }

  const scope = manifest.explorer_scope || {};
  const regionTerms = (scope.region_terms || searchPlan?.region_terms || [])
    .map(term => String(term).trim())
    .filter(Boolean);
  const locCodes = (scope.loc_group_codes || searchPlan?.loc_group_codes || [])
    .map(code => String(code).trim())
    .filter(Boolean);
  const countryCodes = (scope.country_codes || searchPlan?.scan_country_codes || [])
    .map(code => String(code).trim())
    .filter(Boolean);

  let scopeLabel = '';
  if (regionTerms.length > 0) {
    scopeLabel = regionTerms[0];
    if (locCodes.length > 0) {
      scopeLabel = `${scopeLabel} (${locCodes.join(', ')})`;
    }
  } else if (locCodes.length > 0) {
    scopeLabel = locCodes.join(', ');
  } else if (countryCodes.length > 0) {
    scopeLabel = countryCodes.join(', ');
  }

  const sortBy = (manifest.sort_by || searchPlan?.sort_by || 'revenue').trim();
  const scanDepth = manifest.scan_depth || searchPlan?.scan_depth;
  const deepLimit = manifest.deep_dive_limit || searchPlan?.deep_dive_limit || 10;
  const depthLabel =
    scanDepth === 'deep_research'
      ? `Deep research (${deepLimit} markets, 5 credits)`
      : `Standard (${deepLimit} markets)`;

  const planFilters = formatFiltersFragment(searchPlan?.filters);
  const filtersLabel = planFilters || 'none';

  const parts: string[] = [];
  if (scopeLabel) {
    parts.push(scopeLabel);
  }
  parts.push(`Ranked by: ${sortBy} (Explorer)`);
  parts.push(`Depth: ${depthLabel}`);
  parts.push(`Filters: ${filtersLabel}`);
  return parts.join(' · ');
};

export const formatDataBasisLine = (
  metadata?: AiSearchResponseMetadata,
  searchPlan?: AiSearchSearchPlan
): string | null => {
  if (searchPlan?.category === 'market_scan') {
    const scanLine = formatScanManifestLine(metadata, searchPlan);
    if (scanLine) {
      return scanLine;
    }
  }
  const executionSummary = getExecutionSummary(metadata);
  const executionCalls = executionSummary?.market_calls || [];

  if (executionCalls.length > 0) {
    const marketLabels = executionCalls
      .slice(0, 3)
      .map(call => formatMarketBasisLabel(call, searchPlan))
      .filter(Boolean);
    const filtersText = formatFiltersFragment(executionCalls[0]?.filters);
    const microLocationText = formatMicroLocationBasis(metadata, searchPlan);
    if (marketLabels.length === 0 && !filtersText && !microLocationText) {
      return null;
    }
    const basisParts: string[] = [];
    if (marketLabels.length > 0) {
      basisParts.push(marketLabels.join(' · '));
    }
    if (microLocationText) {
      basisParts.push(microLocationText);
    }
    const basisPart = basisParts.join(' · ');
    if (filtersText && basisPart) {
      return `${basisPart} · Filters: ${filtersText}`;
    }
    if (filtersText) {
      return `Filters: ${filtersText}`;
    }
    return basisPart;
  }

  const resolved = searchPlan?.market_resolution?.resolved_markets || [];
  if (resolved.length === 0) {
    return null;
  }

  const marketLabels = resolved
    .slice(0, 3)
    .map(market => {
      const name = (market.name || '').trim();
      const country = (market.country || '').trim();
      if (name && country) {
        return `${name}, ${country}`;
      }
      return name;
    })
    .filter(Boolean);
  const planFilters = formatFiltersFragment(searchPlan?.filters);
  const microLocationText = formatMicroLocationBasis(metadata, searchPlan);
  if (marketLabels.length === 0 && !planFilters && !microLocationText) {
    return null;
  }
  const basisParts: string[] = [];
  if (marketLabels.length > 0) {
    basisParts.push(marketLabels.join(' · '));
  }
  if (microLocationText) {
    basisParts.push(microLocationText);
  }
  const basisPart = basisParts.join(' · ');
  if (planFilters && basisPart) {
    return `${basisPart} · Filters: ${planFilters}`;
  }
  if (planFilters) {
    return `Filters: ${planFilters}`;
  }
  return basisPart;
};

export const formatMarketAnalysisProgressHint = (searchPlan?: AiSearchSearchPlan): string => {
  const micro = searchPlan?.micro_location;
  const coordinatesText = formatCoordinatePair(micro?.latitude, micro?.longitude);
  if (coordinatesText) {
    return `No market_id found yet. Using micro-location ${coordinatesText} and running radius sampling...`;
  }
  return 'No market_id found yet. Inferring city coordinates and sampling nearby listings...';
};

const extractContextualLinks = (text: string): { bodyText: string; links: ContextualLink[] } => {
  const lines = text.split('\n');
  const links: ContextualLink[] = [];
  const bodyLines: string[] = [];

  lines.forEach(line => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.toLowerCase() === 'related links:') {
      if (!trimmed) {
        bodyLines.push(line);
      }
      return;
    }

    const match = trimmed.match(/^- (.+?):\s+(https?:\/\/\S+)$/);
    if (match) {
      links.push({ label: match[1], url: match[2] });
      return;
    }

    bodyLines.push(line);
  });

  return {
    bodyText: bodyLines.join('\n').trim(),
    links,
  };
};

const buildRecentConversationTurns = (messages: ChatMessage[]): AiSearchConversationTurn[] => {
  const turns: AiSearchConversationTurn[] = [];
  for (const message of messages) {
    const text = (message.text || '').trim();
    if (!text) {
      continue;
    }
    turns.push({ role: message.role, text });
  }
  if (turns.length <= CONVERSATION_HISTORY_LIMIT) {
    return turns;
  }
  return turns.slice(-CONVERSATION_HISTORY_LIMIT);
};

type MarketScanCombinedFollowUpProps = {
  context: MarketScanCombinedContext;
  disabled: boolean;
  onSubmit: (choice: AiSearchMarketScanCombinedChoice) => void;
};

const MarketScanCombinedFollowUp: React.FC<MarketScanCombinedFollowUpProps> = ({
  context,
  disabled,
  onSubmit,
}) => {
  const [selectedCodes, setSelectedCodes] = useState<string[]>(context.countryCodes);
  const [selectedSortBy, setSelectedSortBy] = useState<AiSearchSortBy | ''>(context.selectedSortBy);

  useEffect(() => {
    setSelectedCodes(context.countryCodes);
  }, [context.countryCodes]);
  useEffect(() => {
    setSelectedSortBy(context.selectedSortBy);
  }, [context.selectedSortBy]);

  const toggleCountry = (code: string) => {
    setSelectedCodes(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const canSubmit = selectedCodes.length > 0 && selectedSortBy !== '' && !disabled;
  const handleDepth = (depth: AiSearchScanDepth) => {
    if (!canSubmit) {
      return;
    }
    onSubmit({
      country_codes: selectedCodes,
      sort_by: selectedSortBy as AiSearchSortBy,
      scan_depth: depth,
    });
  };

  return (
    <Stack spacing={1.1} data-testid="market-scan-combined-followup" sx={{ mt: 0.5 }}>
      <Typography variant="caption" sx={{ color: COLOR_TEXT_GREY, fontWeight: 700 }}>
        Confirm countries
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
        {selectedCodes.length === 0 ? (
          <Typography variant="caption" sx={{ color: COLOR_TEXT_GREY }}>
            No countries selected — pick at least one below.
          </Typography>
        ) : (
          selectedCodes.map(code => (
            <Chip
              key={`combined-country-${code}`}
              label={formatCountryChipLabel(code)}
              size="small"
              onDelete={disabled ? undefined : () => toggleCountry(code)}
              sx={{ bgcolor: COLOR_PRIMARY_2_100, color: COLOR_PRIMARY_2, fontWeight: 600 }}
            />
          ))
        )}
      </Box>
      {context.countryCodes.some(code => !selectedCodes.includes(code)) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
          {context.countryCodes
            .filter(code => !selectedCodes.includes(code))
            .map(code => (
              <Chip
                key={`combined-country-add-${code}`}
                label={`+ ${formatCountryChipLabel(code)}`}
                size="small"
                variant="outlined"
                disabled={disabled}
                onClick={() => toggleCountry(code)}
                sx={{ borderColor: COLOR_PRIMARY_2_200, color: COLOR_PRIMARY_2 }}
              />
            ))}
        </Box>
      )}

      <Typography variant="caption" sx={{ color: COLOR_TEXT_GREY, fontWeight: 700, mt: 0.5 }}>
        Rank markets by
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
        {context.sortByOptions.map(option => {
          const active = selectedSortBy === option;
          return (
            <Chip
              key={`combined-sort-${option}`}
              label={formatSortByChipLabel(option)}
              size="small"
              variant={active ? 'filled' : 'outlined'}
              disabled={disabled}
              onClick={() => setSelectedSortBy(option)}
              sx={{
                bgcolor: active ? COLOR_PRIMARY_2 : 'transparent',
                color: active ? '#FFFFFF' : COLOR_PRIMARY_2,
                borderColor: COLOR_PRIMARY_2_200,
                fontWeight: 600,
              }}
            />
          );
        })}
      </Box>

      <Typography variant="caption" sx={{ color: COLOR_TEXT_GREY, fontWeight: 700, mt: 0.5 }}>
        Scan depth
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.9}>
        {context.depthOptions.includes('standard') && (
          <Button
            variant="contained"
            size="small"
            disabled={!canSubmit}
            onClick={() => handleDepth('standard')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: COLOR_PRIMARY_2,
              '&:hover': { bgcolor: COLOR_PRIMARY_1 },
            }}
          >
            Standard scan (10 markets, 1 credit)
          </Button>
        )}
        {context.depthOptions.includes('deep_research') && (
          <Button
            variant="outlined"
            size="small"
            disabled={!canSubmit}
            onClick={() => handleDepth('deep_research')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: COLOR_PRIMARY_2,
              color: COLOR_PRIMARY_2,
            }}
          >
            Deep research (100 markets, 5 credits)
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

type MarketClarificationFollowUpProps = {
  context: MarketClarificationContext;
  disabled: boolean;
  onSubmit: (choice: AiSearchMarketClarificationChoice) => void;
};

const MarketClarificationFollowUp: React.FC<MarketClarificationFollowUpProps> = ({
  context,
  disabled,
  onSubmit,
}) => (
  <Stack spacing={1.1} data-testid="market-clarification-followup" sx={{ mt: 0.5 }}>
    {context.suggestions.length === 0 ? (
      <Typography variant="caption" sx={{ color: COLOR_TEXT_GREY }}>
        No close matches were found. Try a more specific city or named market.
      </Typography>
    ) : (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
        {context.suggestions.map(suggestion => {
          const label = [suggestion.name, suggestion.country]
            .filter(Boolean)
            .join(', ');
          return (
            <Chip
              key={`market-clarification-${suggestion.market_id}`}
              label={label || `Market ${suggestion.market_id}`}
              size="small"
              variant="outlined"
              disabled={disabled}
              onClick={() =>
                onSubmit({
                  market_id: suggestion.market_id,
                  name: suggestion.name,
                  country_code: suggestion.country_code,
                  country: suggestion.country,
                  region: suggestion.region,
                })
              }
              sx={{
                borderColor: COLOR_PRIMARY_2_200,
                color: COLOR_PRIMARY_2,
                fontWeight: 600,
              }}
            />
          );
        })}
      </Box>
    )}
  </Stack>
);

const HomeChatPage = ({ user, matches, isLoggedIn, setLoginModalOpen }: HomeChatPageProps) => {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [activeQuestion, setActiveQuestion] = useState('');
  const [isExamplePromptInput, setIsExamplePromptInput] = useState(false);
  const [activeSearchPlan, setActiveSearchPlan] = useState<AiSearchSearchPlan | null>(null);
  const [pendingFollowUpContext, setPendingFollowUpContext] = useState<AiSearchFollowUpContext | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [activeDeadlineAt, setActiveDeadlineAt] = useState<number | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const activeResolvedMarketNames = getResolvedMarketNames(activeSearchPlan || undefined);

  const resetActiveRequest = useCallback(() => {
    setIsPolling(false);
    setActiveMessageId(null);
    setActiveQuestion('');
    setActiveSearchPlan(null);
    setPendingFollowUpContext(null);
    setActiveDeadlineAt(null);
  }, []);

  const handlePollFailure = useCallback(
    (message: string) => {
      setErrorText(message);
      resetActiveRequest();
    },
    [resetActiveRequest]
  );

  useEffect(() => {
    if (!activeMessageId || !isPolling) {
      return;
    }

    let cancelled = false;

    const scheduleNextPoll = () => {
      pollingTimeoutRef.current = setTimeout(() => {
        void pollResult();
      }, 2000);
    };

    const pollResult = async () => {
      try {
        const result = await readAiSearchMessage(activeMessageId);
        if (cancelled) {
          return;
        }

        if (!result.ok) {
          handlePollFailure(result.error);
          return;
        }

        const response = result.data;
        setActiveSearchPlan(response.search_plan || null);

        const isPastDeadline =
          typeof response.deadline_at === 'number' && Date.now() > response.deadline_at * 1000;
        if (
          isPastDeadline &&
          (response.status === 'queued' || response.status === 'processing')
        ) {
          handlePollFailure('Request timed out. Please try again.');
          return;
        }

        if (response.status === 'queued' || response.status === 'processing') {
          scheduleNextPoll();
          return;
        }

        if (response.status === 'completed') {
          setPendingFollowUpContext(getFollowUpContext(response.metadata));
          setMessages(prev => [
            ...prev,
            {
              id: `${response.message_id}-assistant`,
              role: 'assistant',
              text: response.answer || 'No answer was returned.',
              metadata: response.metadata,
              searchPlan: response.search_plan,
            },
          ]);
          setIsPolling(false);
          setActiveMessageId(null);
          setActiveQuestion('');
          setActiveSearchPlan(null);
          setActiveDeadlineAt(null);
          return;
        }

        const errorMessage = response.error?.message || 'AI request failed. Please try again.';
        handlePollFailure(errorMessage);
      } catch {
        if (!cancelled) {
          handlePollFailure('Something went wrong while loading the answer. Please try again.');
        }
      }
    };

    if (
      typeof activeDeadlineAt === 'number' &&
      Date.now() > activeDeadlineAt * 1000
    ) {
      handlePollFailure('Request timed out. Please try again.');
      return;
    }

    scheduleNextPoll();

    return () => {
      cancelled = true;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [activeMessageId, isPolling, activeDeadlineAt, handlePollFailure]);

  const lastAssistantMessageId = messages.reduce<string | null>((lastId, message) => {
    if (message.role === 'assistant') {
      return message.id;
    }
    return lastId;
  }, null);

  const handleSubmitQuestion = async (
    overrideQuestion?: string,
    overrideRequestContext?: AiSearchRequestContext
  ) => {
    if (!isLoggedIn) {
      setLoginModalOpen(true);
      return;
    }

    const question = (overrideQuestion || inputValue).trim();
    const suppressSlack = isExamplePromptInput;
    if (!question || isPolling) {
      return;
    }

    setErrorText('');
    const recentTurns = buildRecentConversationTurns(messages);
    setMessages(prev => [
      ...prev,
      {
        id: `${Date.now()}-user`,
        role: 'user',
        text: question,
      },
    ]);
    setInputValue('');
    setActiveQuestion(question);
    setIsPolling(true);
    setActiveSearchPlan(null);

    try {
      const requestContext =
        overrideRequestContext ??
        (pendingFollowUpContext ? { follow_up_context: pendingFollowUpContext } : undefined);
      const createResult = await createAiSearchMessage(question, {
        context: requestContext,
        conversation_history: recentTurns,
        suppress_slack: suppressSlack,
      });
      if (!createResult.ok) {
        setErrorText(createResult.error);
        resetActiveRequest();
        setIsExamplePromptInput(false);
        return;
      }

      setIsExamplePromptInput(false);

      if (createResult.data.status === 'completed') {
        const completedAnswer = (createResult.data.answer || '').trim();
        const completedMetadata = createResult.data.metadata;
        if (completedAnswer || completedMetadata) {
          setPendingFollowUpContext(getFollowUpContext(completedMetadata));
          setMessages(prev => [
            ...prev,
            {
              id: `${createResult.data.message_id}-assistant`,
              role: 'assistant',
              text: completedAnswer || 'No answer was returned.',
              metadata: completedMetadata,
              searchPlan: createResult.data.search_plan,
            },
          ]);
          resetActiveRequest();
          return;
        }
      }

      const messageId = createResult.data.message_id?.trim();
      if (!messageId) {
        setErrorText('Invalid response from server. Please try again.');
        resetActiveRequest();
        return;
      }

      setActiveMessageId(messageId);
      setActiveSearchPlan(createResult.data.search_plan || null);
      setActiveDeadlineAt(
        typeof createResult.data.deadline_at === 'number' ? createResult.data.deadline_at : null
      );
      setPendingFollowUpContext(null);
    } catch {
      setErrorText('Something went wrong. Please try again.');
      resetActiveRequest();
      setIsExamplePromptInput(false);
    }
  };

  const submitMarketScanDepthChoice = (choice: 'standard' | 'deep_research', label: string) => {
    void handleSubmitQuestion(label, {
      scan_depth: choice,
      follow_up_context: { type: 'market_scan_depth', choice },
    });
  };

  const submitMarketScanCombinedChoice = (choice: AiSearchMarketScanCombinedChoice) => {
    const countriesLabel = choice.country_codes
      .map(code => formatCountryChipLabel(code))
      .filter(Boolean)
      .join(', ');
    const label = `Scan: ${countriesLabel || 'selected countries'} (${
      choice.scan_depth === 'deep_research' ? 'deep research' : 'standard scan'
    })`;
    void handleSubmitQuestion(label, {
      scan_depth: choice.scan_depth,
      follow_up_context: {
        type: 'market_scan_combined',
        country_codes: choice.country_codes,
        country_codes_editable: true,
        selected_sort_by: choice.sort_by,
        sort_by_options: [...AI_SEARCH_SORT_BY_VALUES],
        depth_options: ['standard', 'deep_research'],
        choice,
      },
    });
  };

  const submitMarketClarificationChoice = (choice: AiSearchMarketClarificationChoice) => {
    const label = choice.name?.trim() || `Market ID ${choice.market_id}`;
    void handleSubmitQuestion(label, {
      follow_up_context: {
        type: 'market_clarification',
        suggestions: [],
        choice,
      },
    });
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 2,
        py: { xs: 3, md: 7 },
        overflow: 'auto',
        background: `linear-gradient(180deg, ${COLOR_BACKGROUND_1} 0%, ${COLOR_BACKGROUND_2} 42%)`,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 720 }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={2}
          sx={{ mb: { xs: 3, md: 4 } }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant={matches ? 'h5' : 'h4'}
              component="h1"
              sx={{
                fontWeight: 700,
                color: COLOR_TEXT_2,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              Ask Airbtics
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: COLOR_TEXT_GREY,
                mt: 1,
                maxWidth: 440,
                lineHeight: 1.65,
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              }}
            >
              Short-term rental intelligence, grounded in market data.
            </Typography>
          </Box>
          <Chip
            size="small"
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <span>Credits:</span>
                <AllInclusiveRoundedIcon sx={{ fontSize: 16 }} />
              </Stack>
            }
            sx={{
              flexShrink: 0,
              mt: 0.25,
              fontWeight: 600,
              bgcolor: COLOR_PRIMARY_2_100,
              color: COLOR_PRIMARY_2,
              border: `1px solid ${COLOR_PRIMARY_2_200}`,
              '& .MuiChip-label': { px: 1.25 },
            }}
          />
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            We&apos;re beta testing Ask Airbtics until June 30.
          </Typography>
          <Typography variant="body2">
            During the beta, usage is free. Please try different STR questions and help us improve
            the assistant.
          </Typography>
        </Alert>

        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            bgcolor: COLOR_BACKGROUND_2,
            border: '1px solid #E0E0E0',
            boxShadow: '0 2px 12px 0 rgba(16,30,54,.06)',
            p: { xs: 2, sm: 2.75 },
            pt: { xs: 2.25, sm: 2.5 },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              bgcolor: COLOR_PRIMARY_2,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            },
          }}
        >
          {messages.length > 0 && (
            <Stack spacing={1.25} sx={{ mb: 2 }}>
              {messages.map(message => (
                <Box
                  key={message.id}
                  sx={{
                    alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '92%',
                    borderRadius: 2,
                    px: 1.75,
                    py: 1.1,
                    bgcolor: message.role === 'user' ? COLOR_PRIMARY_1_100 : '#F4F7FA',
                    border:
                      message.role === 'assistant' ? `1px solid ${COLOR_PRIMARY_2_200}` : 'none',
                    boxShadow:
                      message.role === 'assistant'
                        ? '0 8px 22px rgba(3, 122, 144, 0.08)'
                        : 'none',
                  }}
                >
                  {message.role === 'assistant' ? (
                    (() => {
                      const { bodyText, links } = extractContextualLinks(message.text);
                      const isNotSupported = isNotSupportedSearchPlan(message.searchPlan);
                      const dataBasisLine = getDataBasisLineForMessage(message.metadata, message.searchPlan);
                      const marketScanDepthFollowUp = getMarketScanDepthFollowUp(message.metadata);
                      const showDepthChoiceButtons =
                        Boolean(marketScanDepthFollowUp?.awaiting_choice) &&
                        message.id === lastAssistantMessageId &&
                        !isPolling;
                      const marketScanCombinedFollowUp = getMarketScanCombinedFollowUp(message.metadata);
                      const showMarketScanCombinedFollowUp =
                        Boolean(marketScanCombinedFollowUp) &&
                        message.id === lastAssistantMessageId &&
                        !isPolling;
                      const marketClarificationFollowUp = getMarketClarificationFollowUp(message.metadata);
                      const showMarketClarificationFollowUp =
                        Boolean(marketClarificationFollowUp) &&
                        message.id === lastAssistantMessageId &&
                        !isPolling;
                      const occupancyInsight = extractOccupancyInsight(
                        bodyText || message.text,
                        message.metadata
                      );
                      const coordinatesFallback = getCoordinatesFallback(message.metadata);
                      const microLocationContexts = getMicroLocationContexts(message.metadata);
                      const interpretedBookedNights = occupancyInsight
                        ? Math.round((occupancyInsight.occupancyRate / 100) * 30)
                        : null;
                      if (isNotSupported) {
                        return (
                          <Stack spacing={1.1}>
                            <Typography variant="body2" sx={{ color: COLOR_TEXT_2, fontWeight: 600 }}>
                              I&apos;m not sure how to answer that yet.
                            </Typography>
                            <Typography variant="body2" sx={{ color: COLOR_TEXT_2, lineHeight: 1.55 }}>
                              Ask Airbtics works best for short-term rental data questions - market
                              discovery, market analysis, property analysis, and STR metric definitions.
                            </Typography>
                            <Box
                              sx={{
                                p: { xs: 1.2, sm: 1.35 },
                                borderRadius: 1.75,
                                bgcolor: '#F9FBFF',
                                border: `1px solid ${COLOR_PRIMARY_2_200}`,
                              }}
                            >
                              <Typography variant="body2" sx={{ color: COLOR_PRIMARY_2, fontWeight: 700 }}>
                                Try asking:
                              </Typography>
                              <Stack spacing={0.8} sx={{ mt: 0.9 }}>
                                {UNSUPPORTED_EXAMPLE_PROMPTS.map(prompt => (
                                  <Button
                                    key={`${message.id}-${prompt}`}
                                    variant="outlined"
                                    size="small"
                                    disabled={isPolling}
                                    onClick={() => {
                                      setInputValue(prompt);
                                      setIsExamplePromptInput(true);
                                    }}
                                    sx={{
                                      justifyContent: 'flex-start',
                                      textTransform: 'none',
                                      borderColor: COLOR_PRIMARY_2_200,
                                      color: COLOR_PRIMARY_2,
                                      fontWeight: 600,
                                      borderRadius: 1.5,
                                      px: 1.25,
                                      py: 0.75,
                                      background: '#FFFFFF',
                                      '&:hover': {
                                        borderColor: COLOR_PRIMARY_2,
                                        bgcolor: '#F0FAFD',
                                      },
                                    }}
                                  >
                                    {prompt}
                                  </Button>
                                ))}
                              </Stack>
                            </Box>
                          </Stack>
                        );
                      }

                      return (
                        <Stack spacing={1.2}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: COLOR_TEXT_2,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {bodyText || message.text}
                          </Typography>
                          {dataBasisLine && (
                            <Typography
                              variant="caption"
                              sx={{ color: COLOR_TEXT_GREY, display: 'block', lineHeight: 1.45 }}
                            >
                              <Box component="span" sx={{ fontWeight: 700 }}>
                                Data basis:
                              </Box>{' '}
                              {dataBasisLine}
                            </Typography>
                          )}
                          {showDepthChoiceButtons && (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.9} sx={{ mt: 0.5 }}>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={isPolling}
                                onClick={() => submitMarketScanDepthChoice('standard', 'Standard scan')}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  bgcolor: COLOR_PRIMARY_2,
                                  '&:hover': { bgcolor: COLOR_PRIMARY_1 },
                                }}
                              >
                                Standard scan (10 markets, 1 credit)
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={isPolling}
                                onClick={() =>
                                  submitMarketScanDepthChoice('deep_research', 'Deep research scan')
                                }
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  borderColor: COLOR_PRIMARY_2,
                                  color: COLOR_PRIMARY_2,
                                }}
                              >
                                Deep research (100 markets, 5 credits)
                              </Button>
                            </Stack>
                          )}
                          {showMarketScanCombinedFollowUp && marketScanCombinedFollowUp && (
                            <MarketScanCombinedFollowUp
                              context={marketScanCombinedFollowUp}
                              disabled={isPolling}
                              onSubmit={submitMarketScanCombinedChoice}
                            />
                          )}
                          {showMarketClarificationFollowUp && marketClarificationFollowUp && (
                            <MarketClarificationFollowUp
                              context={marketClarificationFollowUp}
                              disabled={isPolling}
                              onSubmit={submitMarketClarificationChoice}
                            />
                          )}
                          {occupancyInsight && interpretedBookedNights !== null && (
                            <Box
                              sx={{
                                p: { xs: 1.15, sm: 1.4 },
                                borderRadius: 1.75,
                                bgcolor: '#F9FBFF',
                                border: '1px solid #E0EAF5',
                              }}
                            >
                              <Typography variant="caption" sx={{ color: COLOR_TEXT_GREY }}>
                                Timeframe
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ mt: 0.2, color: COLOR_TEXT_2, fontWeight: 600 }}
                              >
                                {getTimeframeLabel(occupancyInsight.timeframe)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', mt: 0.35, color: COLOR_TEXT_GREY }}
                              >
                                {getTimeframeConfidenceNote(
                                  occupancyInsight.timeframe,
                                  occupancyInsight.timeframeSource
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ mt: 0.85, color: COLOR_TEXT_2, lineHeight: 1.5 }}
                              >
                                That is about {interpretedBookedNights} booked nights per 30-day month.
                              </Typography>
                            </Box>
                          )}
                          {coordinatesFallback?.used && (
                            <Box
                              sx={{
                                p: { xs: 1.1, sm: 1.3 },
                                borderRadius: 1.75,
                                bgcolor: '#F9FBFF',
                                border: '1px solid #E0EAF5',
                              }}
                            >
                              <Typography variant="caption" sx={{ color: COLOR_TEXT_GREY }}>
                                Coordinates fallback
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.2, color: COLOR_TEXT_2, fontWeight: 600 }}>
                                {coordinatesFallback.city || 'Inferred city'}
                                {coordinatesFallback.country ? `, ${coordinatesFallback.country}` : ''}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: COLOR_TEXT_GREY }}>
                                Confidence:{' '}
                                {typeof coordinatesFallback.confidence === 'number'
                                  ? `${Math.round(coordinatesFallback.confidence * 100)}%`
                                  : 'N/A'}
                                {typeof coordinatesFallback.listing_sample_count === 'number'
                                  ? ` · Listing samples: ${coordinatesFallback.listing_sample_count}`
                                  : ''}
                              </Typography>
                            </Box>
                          )}
                          {(() => {
                            // Only render cards that have a real coordinate
                            // pair AND were actually used (sample succeeded).
                            // (0,0) placeholders and used=false fall through.
                            const renderable = microLocationContexts.filter(
                              ctx =>
                                ctx?.used &&
                                Boolean(
                                  formatCoordinatePair(
                                    ctx.coordinates?.latitude,
                                    ctx.coordinates?.longitude
                                  )
                                )
                            );
                            if (renderable.length === 0) {
                              return null;
                            }
                            return (
                              <Stack spacing={1}>
                                {renderable.length > 1 && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: COLOR_TEXT_GREY, fontWeight: 700 }}
                                  >
                                    Micro-location radius search ({renderable.length} sub-areas)
                                  </Typography>
                                )}
                                {renderable.map((ctx, idx) => (
                                  <Box
                                    key={`${ctx.raw_phrase || 'sub-area'}-${idx}`}
                                    sx={{
                                      p: { xs: 1.1, sm: 1.3 },
                                      borderRadius: 1.75,
                                      bgcolor: '#F9FBFF',
                                      border: '1px solid #E0EAF5',
                                    }}
                                  >
                                    {renderable.length === 1 && (
                                      <Typography variant="caption" sx={{ color: COLOR_TEXT_GREY }}>
                                        Micro-location radius search
                                      </Typography>
                                    )}
                                    <Typography
                                      variant="body2"
                                      sx={{ mt: 0.2, color: COLOR_TEXT_2, fontWeight: 600 }}
                                    >
                                      {ctx.raw_phrase || 'Sub-area context'}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ display: 'block', mt: 0.35, color: COLOR_TEXT_GREY }}
                                    >
                                      {formatCoordinatePair(
                                        ctx.coordinates?.latitude,
                                        ctx.coordinates?.longitude
                                      )}
                                      {typeof ctx.selected_radius_meters === 'number'
                                        ? ` · Radius: ${ctx.selected_radius_meters}m`
                                        : ''}
                                      {formatMicroLocationSampleSizeLabel(ctx)
                                        ? ` · ${formatMicroLocationSampleSizeLabel(ctx)}`
                                        : ''}
                                      {formatMicroLocationSampleMetricsLabel(ctx)
                                        ? ` · ${formatMicroLocationSampleMetricsLabel(ctx)}`
                                        : ''}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            );
                          })()}
                          {links.length > 0 && (
                            <Box
                              sx={{
                                p: { xs: 1.25, sm: 1.6 },
                                borderRadius: 2,
                                bgcolor: '#F8FDFF',
                                border: `1px solid ${COLOR_PRIMARY_2_200}`,
                                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.75)',
                              }}
                            >
                              <Stack spacing={0.35}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: COLOR_PRIMARY_2,
                                    fontWeight: 700,
                                  }}
                                >
                                  Explore this market
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: COLOR_TEXT_GREY,
                                    lineHeight: 1.35,
                                  }}
                                >
                                  Source: verified Airbtics market data.
                                </Typography>
                              </Stack>
                              <Stack spacing={0.9} sx={{ mt: 1.1 }}>
                                {links.map(link => (
                                  <Button
                                    key={`${message.id}-${link.url}`}
                                    variant="outlined"
                                    size="small"
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      justifyContent: 'space-between',
                                      textTransform: 'none',
                                      borderColor: COLOR_PRIMARY_2_200,
                                      color: COLOR_PRIMARY_2,
                                      fontWeight: 600,
                                      borderRadius: 1.5,
                                      px: 1.4,
                                      py: 0.8,
                                      background: '#FFFFFF',
                                      transition:
                                        'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease',
                                      '&:hover': {
                                        borderColor: COLOR_PRIMARY_2,
                                        bgcolor: '#F0FAFD',
                                        boxShadow: '0 2px 8px rgba(3,122,144,0.12)',
                                      },
                                    }}
                                  >
                                    {formatMarketCtaLabel(link.label)}
                                    <Typography
                                      component="span"
                                      sx={{
                                        ml: 1,
                                        fontSize: '0.95rem',
                                        color: COLOR_PRIMARY_2,
                                        lineHeight: 1,
                                      }}
                                    >
                                      ↗
                                    </Typography>
                                  </Button>
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      );
                    })()
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: COLOR_TEXT_2,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {message.text}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}

          {isPolling && (
            <Box
              sx={{
                mb: 2,
                borderRadius: 2,
                px: 1.75,
                py: 1.1,
                bgcolor: '#F4F7FA',
                border: '1px solid #E8EAED',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: activeSearchPlan ? 1 : 0 }}>
                <CircularProgress size={15} />
                <Typography variant="body2" sx={{ color: COLOR_TEXT_2 }}>
                  Analyzing...
                </Typography>
              </Stack>
              {activeSearchPlan && (
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: '#FFFFFF',
                    border: `1px solid ${COLOR_PRIMARY_2_200}`,
                  }}
                >
                  <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap flexWrap="wrap">
                    <Chip
                      size="small"
                      label={getCategoryLabel(activeSearchPlan.category)}
                      sx={{
                        bgcolor: COLOR_PRIMARY_2_100,
                        color: COLOR_PRIMARY_2,
                        fontWeight: 600,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: COLOR_TEXT_GREY }}>
                      {activeSearchPlan.stage1_message || 'Stage 1 completed. Running search...'}
                    </Typography>
                  </Stack>
                  {activeResolvedMarketNames.length > 0 && (
                    <Typography variant="body2" sx={{ mt: 0.8, color: COLOR_TEXT_2 }}>
                      Searching {activeResolvedMarketNames.join(', ')}
                      {activeResolvedMarketNames.length >= 10 ? ' (showing up to 10 markets)' : ''}
                      ...
                    </Typography>
                  )}
                  {activeSearchPlan.category === 'market_scan' &&
                    activeResolvedMarketNames.length === 0 && (
                      <Typography variant="body2" sx={{ mt: 0.8, color: COLOR_TEXT_2 }}>
                        Resolving markets for scan...
                      </Typography>
                    )}
                  {activeSearchPlan.category === 'market_analysis' &&
                    activeResolvedMarketNames.length === 0 && (
                      <Typography variant="body2" sx={{ mt: 0.8, color: COLOR_TEXT_2 }}>
                        {formatMarketAnalysisProgressHint(activeSearchPlan)}
                      </Typography>
                    )}
                  {(() => {
                    // Pre-execution preview of the deterministic api_plan.
                    // Renders one row per Public API call that Stage 1 has
                    // already decided will be made (no LLM mid-execution
                    // tool choice). Gives the user full transparency into
                    // exactly which data sources are being fetched.
                    const apiPlanRows = getApiPlanPreviewRows(activeSearchPlan);
                    if (apiPlanRows.length === 0) {
                      return null;
                    }
                    return (
                      <Box
                        data-testid="api-plan-preview"
                        sx={{
                          mt: 1,
                          pt: 1,
                          borderTop: `1px dashed ${COLOR_PRIMARY_2_200}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: COLOR_TEXT_GREY, fontWeight: 600 }}
                        >
                          Fetching {apiPlanRows.length} data {apiPlanRows.length === 1 ? 'source' : 'sources'}
                        </Typography>
                        <Stack spacing={0.5} sx={{ mt: 0.6 }}>
                          {apiPlanRows.map(row => (
                            <Stack
                              key={row.stepId}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              data-testid={`api-plan-step-${row.stepId}`}
                            >
                              <CircularProgress size={10} />
                              <Typography variant="caption" sx={{ color: COLOR_TEXT_2 }}>
                                <strong>{row.toolLabel}:</strong> {row.label}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    );
                  })()}
                </Box>
              )}
            </Box>
          )}

          {errorText && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setErrorText('');
                    inputRef.current?.focus();
                  }}
                >
                  Retry
                </Button>
              }
            >
              {errorText}
            </Alert>
          )}

          {!isLoggedIn && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Sign up or log in to use Ask Airbtics.
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            minRows={matches ? 2 : 3}
            inputRef={inputRef}
            placeholder="Ask any short-term rental related questions"
            value={inputValue}
            onChange={e => {
              setInputValue(e.target.value);
              setIsExamplePromptInput(false);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void handleSubmitQuestion();
              }
            }}
            variant="outlined"
            disabled={isPolling || !isLoggedIn}
            sx={{
              mt: 0.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                bgcolor: '#FAFBFC',
                fontSize: '1rem',
                lineHeight: 1.5,
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                '& fieldset': { borderColor: '#E8EAED' },
                '&:hover': {
                  bgcolor: COLOR_BACKGROUND_2,
                  '& fieldset': { borderColor: COLOR_PRIMARY_1 },
                },
                '&.Mui-focused': {
                  bgcolor: COLOR_BACKGROUND_2,
                  boxShadow: `0 0 0 3px ${COLOR_PRIMARY_1_100}`,
                },
                '&.Mui-focused fieldset': {
                  borderColor: COLOR_PRIMARY_1,
                  borderWidth: 2,
                },
              },
              '& .MuiOutlinedInput-input::placeholder': {
                color: COLOR_TEXT_GREY,
                opacity: 1,
              },
            }}
          />

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
            <Button
              variant="contained"
              onClick={() => void handleSubmitQuestion()}
              disabled={!isLoggedIn || !inputValue.trim() || isPolling}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
              }}
            >
              {isPolling ? 'Searching...' : 'Search'}
            </Button>
          </Stack>

          <Typography
            variant="subtitle2"
            sx={{
              mt: 3,
              mb: 1.5,
              color: COLOR_TEXT_GREY,
              fontWeight: 600,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              fontSize: '0.7rem',
            }}
          >
            Try an example
          </Typography>

          <Grid container spacing={1.5}>
            {EXAMPLE_PROMPTS.map(text => (
              <Grid item xs={12} sm={matches ? 12 : 6} key={text}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    if (!isLoggedIn) {
                      setLoginModalOpen(true);
                      return;
                    }
                    setInputValue(text);
                    setIsExamplePromptInput(true);
                  }}
                  disabled={isPolling || !isLoggedIn}
                  sx={{
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    borderColor: '#E8EAED',
                    color: COLOR_PRIMARY_2,
                    py: 1.35,
                    px: 2,
                    borderRadius: '12px',
                    whiteSpace: 'normal',
                    fontSize: '0.875rem',
                    lineHeight: 1.45,
                    transition: 'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      borderColor: COLOR_PRIMARY_1,
                      bgcolor: COLOR_PRIMARY_1_100,
                      boxShadow: '0 1px 6px rgba(3,122,144,0.08)',
                    },
                  }}
                >
                  {text}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default HomeChatPage;
